import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { updateOnboardingSession } from '../../../../services/wizardApi';
import { toast } from 'react-toast';
import { LuCheck, LuCode, LuChevronDown, LuChevronUp, LuPlus, LuTrash, LuPencil, LuSave, LuX, LuFileCode, LuPlay, LuLoader } from 'react-icons/lu';
import Editor from '@monaco-editor/react';
import axiosInstance from '../../../../api/axiosInstance';
import RunTestCaseModal from '../../../test-suites/RunTestCaseModal';
import '../../../../styles/wizard.css';
import { STEP_STATUS } from '../../../../Config/Onboarding/constant';

const Step10BReviewRecording = () => {
  const navigate = useNavigate();
  const { recorder, setRecorder, sessionId, project, data, markStepCompleted, setStepStatus, setTestCases:contextTestCases, goToStep } = useOnboarding();
  const [expandedScenario, setExpandedScenario] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [testCaseToDelete, setTestCaseToDelete] = useState(null);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptContent, setScriptContent] = useState('');
  const [isRunTestCaseModalOpen, setIsRunTestCaseModalOpen] = useState(false);
  const [selectedTestCaseForRun, setSelectedTestCaseForRun] = useState(null);

  // API Integration States
  const [testCases, setTestCases] = useState([]);
  const [isLoadingTestCases, setIsLoadingTestCases] = useState(true);
  const [testCasesError, setTestCasesError] = useState(null);
  const [isLoadingScript, setIsLoadingScript] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const projectId = data?.project?.project_id || 281;

  // Fetch test cases from API
  useEffect(() => {
    const fetchTestCases = async () => {
      setIsLoadingTestCases(true);
      setTestCasesError(null);
      try {
        const response = await axiosInstance.get(
          `/test_case/cases_list?project_id=${projectId}&page=1&page_size=10`
        );
        
        if (response?.data) {
          setTestCases(response.data);
        }
      } catch (error) {
        console.error('Error fetching test cases:', error);
        setTestCasesError('Failed to fetch test cases');
        toast.error('Failed to fetch test cases');
      } finally {
        setIsLoadingTestCases(false);
      }
    };

    fetchTestCases();
  }, [projectId]);

  // Fetch automation script from API
  const fetchAutomationScript = useCallback(async (testCaseId) => {
    setIsLoadingScript(true);
    try {
      const response = await axiosInstance.get(
        `/execute_file/get_automation_script/${testCaseId}`
      );
      
      if (response) {
        return response;
      }
      return generateDefaultScript(testCaseId);
    } catch (error) {
      console.error('Error fetching automation script:', error);
      return generateDefaultScript(testCaseId);
    } finally {
      setIsLoadingScript(false);
    }
  }, []);

  // Generate default script if API fails
  const generateDefaultScript = (testCaseId) => {
    return `// Test Case ID: ${testCaseId}
// Generated by Robitest Automation Engine
// Framework: Playwright

import { test, expect } from '@playwright/test';

test.describe('Recorded Test Case', () => {
  test('should execute recorded test', async ({ page }) => {
    // Add your test steps here
    console.log('Test case executed');
  });
});`;
  };

  const handleContinueToRun = async () => {
    // Test cases already saved during recording, just proceed
     const testCaseIds = testCases.map(tc => tc.test_case_id);
     contextTestCases(testCaseIds); 
    try {
      // Review is step 8; run test is step 9
      markStepCompleted(8);
      setStepStatus(9, STEP_STATUS.IN_PROGRESS);

      if (sessionId) {
        await updateOnboardingSession(sessionId, {
          currentStep: 9
        });
      }

      toast.success(`${testCases.length} test case(s) ready to run!`);
      goToStep(9);
      navigate('/app/onboarding?step=9&mode=recorder');
    } catch (error) {
      console.error('Error proceeding:', error);
      toast.error('Failed to proceed');
    }
  };

  const handleRecordMore = () => {
    goToStep(7);
    navigate('/app/onboarding?step=7&mode=recorder');
  };

  const handleDeleteClick = (testCaseId) => {
    // Find the test case to get the full object
    const testCase = testCases.find(tc => tc.test_case_id === testCaseId);
    setTestCaseToDelete(testCase);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!testCaseToDelete) return;
    
    try {
      // Call the delete API
      const response = await axiosInstance.delete(`/test_case/${testCaseToDelete.test_case_id}`);
      
      if (response?.status_code === 200) {
        // Remove the test case from the local state
        const updatedTestCases = testCases.filter(tc => tc.test_case_id !== testCaseToDelete.test_case_id);
        setTestCases(updatedTestCases);
        setShowDeleteConfirm(false);
        setTestCaseToDelete(null);
        toast.success('Test case deleted successfully');
        
        // If no test cases left, go back to recording
        if (updatedTestCases.length === 0) {
          toast.info('No test cases remaining. Redirecting to recording...');
          setTimeout(() => navigate('/app/onboarding?step=7&mode=recorder'), 1500);
        }
      } else {
        toast.error('Failed to delete test case');
      }
    } catch (error) {
      console.error('Error deleting test case:', error);
      toast.error(error.response?.data?.message || 'Failed to delete test case');
      setShowDeleteConfirm(false);
      setTestCaseToDelete(null);
    }
  };

  const handleEditClick = (testCase) => {
    setEditingTestCase(testCase.test_case_id);
    setEditValue(testCase.name);
  };

  const handleSaveEdit = () => {
    if (!editValue.trim()) {
      toast.error('Test case name cannot be empty');
      return;
    }

    const updatedTestCases = testCases.map(tc =>
      tc.test_case_id === editingTestCase
        ? { ...tc, name: editValue.trim() }
        : tc
    );
    
    setTestCases(updatedTestCases);
    setEditingTestCase(null);
    setEditValue('');
    toast.success('Test case name updated');
  };

  const handleCancelEdit = () => {
    setEditingTestCase(null);
    setEditValue('');
  };

  const handleViewScript = async (testCase) => {
    setSelectedScript(testCase);
    setIsLoadingScript(true);
    const script = await fetchAutomationScript(testCase.test_case_id);
    setScriptContent(script);
    setShowScriptModal(true);
  };

  const handleSaveScript = () => {
    toast.success('Script saved successfully');
    setShowScriptModal(false);
  };

  const handleCloseScriptModal = () => {
    setShowScriptModal(false);
    setSelectedScript(null);
    setScriptContent('');
  };

  const handleRunTestCase = (testCase) => {
    setSelectedTestCaseForRun(testCase);
    setIsRunTestCaseModalOpen(true);
  };

  const handleCloseRunTestCaseModal = () => {
    setIsRunTestCaseModalOpen(false);
    setSelectedTestCaseForRun(null);
  };

  const handleCreateNewTestCase = () => {

    // Reset recorder state to allow recording again
    // Clear all the saved form fields
    setRecorder({
      ...recorder,
      hasRecorded: false,
      recordingSessionId: null,
      testCaseName: '',
      description: '',
      priority: 'medium',
      recordingStatus: null
    });
    goToStep(7);
    // Navigate back to step 7 (Recording Setup)
    navigate('/app/onboarding?step=7&mode=recorder');
  };

  // Format steps description
  const getStepsCount = (testCase) => {
    if (testCase?.steps?.step_description && Array.isArray(testCase.steps.step_description)) {
      return testCase.steps.step_description.length;
    }
    return 0;
  };

  // Get steps descriptions
  const getStepsDescription = (testCase) => {
    if (testCase?.steps?.step_description && Array.isArray(testCase.steps.step_description)) {
      return testCase.steps.step_description;
    }
    return [];
  };

  if (isLoadingTestCases) {
    return (
      <PrivateWizardLayout>
        <WizardContainer>
          <WizardCard title="Loading Test Cases">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
              <LuLoader size={32} className="spinner" style={{ color: 'var(--primary-color)' }} />
            </div>
          </WizardCard>
        </WizardContainer>
      </PrivateWizardLayout>
    );
  }

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={8} completedSteps={[1, 2, 3, 4, 5, 6, 7]} />
        
        <WizardCard
          title="Recorded Test Cases"
          subtitle={`${testCases.length} test case(s) recorded • Ready for execution`}
        >
          {/* Success Banner - Compact */}
          <div style={{ 
            padding: '0.875rem 1rem', 
            backgroundColor: 'var(--success-color-light)',
            borderRadius: '8px',
            border: '1px solid var(--success-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem'
          }}>
            <LuCheck size={20} style={{ color: 'var(--success-color)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: '0.875rem', color: 'var(--success-color)' }}>Test Cases Ready</strong>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
                {testCases.reduce((sum, tc) => sum + getStepsCount(tc), 0)} total steps • {testCases.length} test cases
              </div>
            </div>
          </div>

          {/* Test Cases List */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>
                Test Cases ({testCases.length})
              </h3>
              <button
                onClick={handleCreateNewTestCase}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--primary-color)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <LuPlus size={18} />
                Create Test Case
              </button>
            </div>
            
            {testCases.length === 0 ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px dashed var(--border-color)',
                color: 'var(--text-secondary)'
              }}>
                <p style={{ margin: 0, marginBottom: '0.5rem' }}>No test cases found</p>
                <button
                  className="wizard-btn wizard-btn-primary"
                  onClick={handleRecordMore}
                  style={{ marginTop: '1rem' }}
                >
                  <LuPlus size={18} />
                  Go Back to Record
                </button>
              </div>
            ) : (
              <div style={{
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr 80px 100px 100px 140px',
                  gap: '1rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: 'var(--card-bg)',
                  borderBottom: '1px solid var(--border-color)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase'
                }}>
                  <div></div>
                  <div>Name</div>
                  <div>Steps</div>
                  <div>Priority</div>
                  <div>Tags</div>
                  <div style={{ textAlign: 'center' }}>Actions</div>
                </div>

                {/* Table Rows */}
                {testCases.map((testCase, index) => (
                  <div key={testCase.test_case_id}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '40px 1fr 80px 100px 100px 140px',
                        gap: '1rem',
                        padding: '0.875rem 1rem',
                        alignItems: 'center',
                        backgroundColor: expandedScenario === index ? 'var(--primary-color-light)' : '#fff',
                        borderBottom: index < testCases.length - 1 ? '1px solid var(--border-color)' : 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => setExpandedScenario(expandedScenario === index ? null : index)}
                      onMouseEnter={(e) => {
                        if (expandedScenario !== index) e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                      }}
                      onMouseLeave={(e) => {
                        if (expandedScenario !== index) e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      {/* Status Icon */}
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--success-color)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <LuCheck size={14} />
                      </div>

                      {/* Test Case Name */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {editingTestCase === testCase.test_case_id ? (
                          <>
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter') handleSaveEdit();
                                if (e.key === 'Escape') handleCancelEdit();
                              }}
                              style={{
                                flex: 1,
                                padding: '0.375rem 0.5rem',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                border: '1px solid var(--primary-color)',
                                borderRadius: '4px',
                                outline: 'none'
                              }}
                              autoFocus
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveEdit();
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'var(--success-color)'
                              }}
                              title="Save"
                            >
                              <LuSave size={16} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelEdit();
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'var(--text-secondary)'
                              }}
                              title="Cancel"
                            >
                              <LuX size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <div style={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {testCase.name}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(testCase);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'var(--text-secondary)',
                                opacity: 0.6,
                                transition: 'opacity 0.2s'
                              }}
                              title="Edit name"
                              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                            >
                              <LuPencil size={14} />
                            </button>
                          </>
                        )}
                      </div>

                      {/* Steps Count */}
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {getStepsCount(testCase)} steps
                      </div>

                      {/* Priority */}
                      <div>
                        <span style={{
                          padding: '0.25rem 0.625rem',
                          backgroundColor: testCase.priority === 'High' ? 'var(--error-color-light)' : 'var(--info-color-light)',
                          color: testCase.priority === 'High' ? 'var(--error-color)' : 'var(--info-color)',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}>
                          {testCase.priority || 'Medium'}
                        </span>
                      </div>

                      {/* Tags */}
                      <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: 'var(--primary-color-light)',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: 'var(--primary-color)',
                          whiteSpace: 'nowrap'
                        }}>
                          Recorded
                        </span>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'center', alignItems: 'center' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRunTestCase(testCase);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.375rem',
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--success-color)',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                          title="Run test case"
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--success-color-light)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <LuPlay size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewScript(testCase);
                          }}
                          style={{
                            background: 'none',
                            border: '1px solid var(--primary-color)',
                            cursor: 'pointer',
                            padding: '0.375rem 0.625rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            color: 'var(--primary-color)',
                            borderRadius: '4px',
                            fontSize: '0.65rem',
                            fontWeight: 600
                          }}
                          title="View/Edit Script"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--primary-color)';
                          }}
                        >
                          <LuFileCode size={12} />
                          Script
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(testCase.test_case_id);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.375rem',
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--error-color)',
                            borderRadius: '4px'
                          }}
                          title="Delete test case"
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--error-color-light)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <LuTrash size={16} />
                        </button>
                        {expandedScenario === index ? <LuChevronUp size={16} /> : <LuChevronDown size={16} />}
                      </div>
                    </div>

                    {/* Expanded Row - Steps Details */}
                    {expandedScenario === index && (
                      <div style={{ padding: '1rem', backgroundColor: 'var(--background-color)' }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>
                          Test Steps ({getStepsCount(testCase)}):
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                          {getStepsDescription(testCase).map((step, stepIndex) => (
                            <div 
                              key={stepIndex}
                              style={{ 
                                display: 'flex',
                                gap: '0.75rem',
                                padding: '0.625rem 0.875rem',
                                backgroundColor: '#ffffff',
                                borderRadius: '6px',
                                border: '1px solid var(--border-color)',
                                fontSize: '0.8125rem'
                              }}
                            >
                              <span style={{ fontWeight: 600, color: 'var(--primary-color)', minWidth: '24px' }}>
                                {stepIndex + 1}.
                              </span>
                              <div style={{ flex: 1 }}>
                                <div>{step.description || step.action || 'Action'}</div>
                                {step.selector && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontFamily: 'monospace', marginTop: '0.25rem' }}>
                                    Selector: {step.selector}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Automation Script Indicator */}
                        <div style={{ 
                          padding: '0.75rem 1rem',
                          backgroundColor: '#1e1e1e',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem'
                        }}>
                          <LuCode size={18} style={{ color: '#4ade80', flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#d4d4d4' }}>
                              Automation Script
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#888' }}>
                              Playwright • {getStepsCount(testCase)} commands
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <WizardActions
            showBack={true}
            onBack={handleRecordMore}
            backLabel={
              <>
                <LuPlus size={18} />
                Record More Test Cases
              </>
            }
            showNext={true}
            onNext={handleContinueToRun}
            nextLabel="Continue to Run Tests"
            nextDisabled={testCases.length === 0}
          />
        </WizardCard>
      </WizardContainer>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              zIndex: 1000000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#fee',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <LuTrash size={24} style={{ color: '#dc3545' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#333' }}>
                  Delete Test Case? 
                </h3>
              </div>
            </div>

            <p style={{ margin: 0, marginBottom: '2rem', color: '#666', fontSize: '0.9375rem' }}>
              This will permanently delete the recorded test case. This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '0.625rem 1.25rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  color: '#333',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  padding: '0.625rem 1.25rem',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#dc3545',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}
              >
                {/* <LuTrash size={16} style={{ verticalAlign: 'middle', marginRight: '0.375rem' }} /> */}
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Script Editor Modal */}
      {showScriptModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '2rem'
          }}
          onClick={handleCloseScriptModal}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '1200px',
              height: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              zIndex: 1000000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#333' }}>
                  <LuFileCode size={24} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Automation Script
                </h3>
                <p style={{ margin: 0, marginTop: '0.25rem', fontSize: '0.875rem', color: '#666' }}>
                  {selectedScript?.name} - Playwright/TypeScript
                </p>
              </div>
              <button
                onClick={handleCloseScriptModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <LuX size={24} />
              </button>
            </div>

            {/* Editor */}
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              {isLoadingScript && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <LuLoader size={32} className="spinner" style={{ color: 'var(--primary-color)' }} />
                </div>
              )}
              <Editor
                height="100%"
                defaultLanguage="typescript"
                value={scriptContent}
                onChange={(value) => setScriptContent(value || '')}
                theme="vs-dark"
                options={{
                  minimap: { enabled: true },
                  fontSize: 14,
                  lineNumbers: 'on',
                  roundedSelection: false,
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  tabSize: 2
                }}
              />
            </div>

            {/* Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              {/* <div style={{ fontSize: '0.875rem', color: '#666' }}>
                <LuCode size={16} style={{ verticalAlign: 'middle', marginRight: '0.375rem' }} />
                You can edit the script and save changes
              </div> */}
              {/* <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={handleCloseScriptModal}
                  style={{
                    padding: '0.625rem 1.25rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    backgroundColor: 'transparent',
                    color: '#333',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveScript}
                  style={{
                    padding: '0.625rem 1.25rem',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#007bff',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem'
                  }}
                >
                  <LuSave size={16} />
                  Save Script
                </button>
              </div> */}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Run Test Case Modal */}
      {selectedTestCaseForRun && (
        <RunTestCaseModal
          isOpen={isRunTestCaseModalOpen}
          onClose={handleCloseRunTestCaseModal}
          testCaseId={selectedTestCaseForRun.test_case_id}
          testCaseName={selectedTestCaseForRun.name}
          projectId={projectId}
          projectName={project?.name || 'Project'}
        />
      )}
    </PrivateWizardLayout>
  );
};

export default Step10BReviewRecording;